import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Fingerprint } from 'lucide-react';
import { requestPasswordReset } from '../../utils/firebase';
import { sanitizeAuthError } from '../../utils/userFacingErrors';

const SUPPORT_MAILTO = 'mailto:team@brightforge.ltd?subject=Pixlie%20Account%20Help';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError('Enter your email address.');
      return;
    }
    setLoading(true);
    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      setError(sanitizeAuthError(err instanceof Error ? err.message : 'Could not send reset email.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-black p-4 text-white">
      <div className="grain-panel w-full max-w-md rounded-3xl border border-white/10 bg-[#0a0a0a] p-8 shadow-2xl">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-secondary">
            <Fingerprint className="h-6 w-6" />
          </div>
          <h2 className="text-2xl font-bold">Reset password</h2>
          <p className="mt-2 text-sm text-white/45">
            We&apos;ll email you a link to choose a new password.
          </p>
        </div>

        {sent ? (
          <div className="space-y-4 text-center">
            <p className="text-sm leading-6 text-white/70">
              If an account exists for <span className="text-secondary">{email}</span>, check your inbox
              (and spam) for the reset link.
            </p>
            <Link to="/login" className="inline-block text-xs uppercase tracking-widest text-secondary hover:underline">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-2 block pl-1 text-[10px] font-bold uppercase tracking-widest text-white/40">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/20" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-white/5 py-3 pl-10 pr-4 font-mono text-sm text-white outline-none transition-all placeholder:text-white/20 focus:border-secondary focus:bg-white/10"
                  placeholder="you@studio.com"
                  disabled={loading}
                  required
                />
              </div>
            </div>
            {error && (
              <p className="rounded border border-red-500/20 bg-red-500/10 p-2 text-xs text-red-300">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              className="mt-4 w-full rounded-xl bg-white py-4 text-xs font-bold uppercase tracking-widest text-black transition-all hover:bg-secondary disabled:opacity-50"
            >
              {loading ? 'Sending…' : 'Send reset link'}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-xs text-white/35">
          Still stuck?{' '}
          <a href={SUPPORT_MAILTO} className="text-secondary hover:underline">
            Contact support
          </a>
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
